// src/components/administration/serviceOrders/client/ClientServiceOrdersDashboard.js
import React, { useMemo, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import GroupsIcon from "@mui/icons-material/Groups";
import RouteIcon from "@mui/icons-material/Route";
import PaidIcon from "@mui/icons-material/Paid";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import { useNavigate } from "react-router-dom";
import { useAuthContext } from "../../../context/AuthProvider";
import { useClientServiceOrders } from "../../../hooks/administration/useServiceOrders";
import { formatDate } from "../../gerencia/report/orderService/utils/formatters";

/* -------------------------------------------------------------------------- */
/*                                   Config                                   */
/* -------------------------------------------------------------------------- */

// Cada estado apunta a una "etapa de pago" que es lo que le interesa al cliente
const ESTADO_CONFIG = {
    CREADO: { label: "CREADO", color: "#90a4ae", text: "#fff", stage: "POR_VALORIZAR" },
    VALIDADA: { label: "VALIDADA", color: "#fbc02d", text: "#000", stage: "POR_VALORIZAR" },
    LIBERADA: { label: "LIBERADA", color: "#4caf50", text: "#fff", stage: "POR_VALORIZAR" },
    EN_EJECUCION: { label: "EN EJECUCIÓN", color: "#1976d2", text: "#fff", stage: "POR_VALORIZAR" },
    FINALIZADA: { label: "FINALIZADA", color: "#388e3c", text: "#fff", stage: "POR_VALORIZAR" },

    PRE_VALORIZADA: { label: "PRE-VALORIZADA", color: "#8e24aa", text: "#fff", stage: "POR_COBRAR" },
    VALORIZADA: { label: "VALORIZADA", color: "#6a1b9a", text: "#fff", stage: "POR_COBRAR" },
    FACTURADA: { label: "FACTURADA", color: "#455a64", text: "#fff", stage: "POR_COBRAR" }, // 👈 CAMBIO

    CERRADA: { label: "CERRADA", color: "#00695c", text: "#fff", stage: "COBRADO" },
    CANCELADA: { label: "CANCELADA", color: "#d32f2f", text: "#fff", stage: "ANULADO" },
};

const VALORIZACION_ESTADO_CONFIG = {
    CREADA: { label: "CREADA", color: "#90a4ae", text: "#fff" },
    EN_PROCESO: { label: "EN PROCESO", color: "#1976d2", text: "#fff" },
    EN_REVISION: { label: "EN REVISIÓN", color: "#fbc02d", text: "#000" },
    APROBADA: { label: "APROBADA", color: "#4caf50", text: "#fff" },
    FACTURADA: { label: "FACTURADA", color: "#455a64", text: "#fff" },
    COBRADA: { label: "COBRADA", color: "#00695c", text: "#fff" },
    CANCELADA: { label: "CANCELADA", color: "#d32f2f", text: "#fff" },
};

const codigoVal = (id) => `VAL-${String(id).padStart(6, "0")}`;

const PAYMENT_STAGES = {
    POR_VALORIZAR: {
        label: "Por valorizar",
        color: "#ed6c02",
        bg: "#fff4e5",
        Icon: HourglassEmptyIcon,
    },
    POR_COBRAR: {
        label: "Por pagar",
        color: "#0288d1",
        bg: "#e5f4fd",
        Icon: ReceiptLongIcon,
    },
    COBRADO: {
        label: "Pagado",
        color: "#2e7d32",
        bg: "#e8f5e9",
        Icon: PaidIcon,
    },
};

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const formatCurrency = (value) =>
    new Intl.NumberFormat("es-PE", {
        style: "currency",
        currency: "PEN",
        minimumFractionDigits: 2,
    }).format(Number(value || 0));

const formatNumber = (value) =>
    new Intl.NumberFormat("es-PE").format(Number(value || 0));

const codigoOS = (id) => `OS-${String(id).padStart(6, "0")}`;

/* -------------------------------------------------------------------------- */
/*                              Sub‑componentes                               */
/* -------------------------------------------------------------------------- */

const MetricCard = ({ label, value, subtitle, icon: Icon, accent = "#1976d2" }) => (
    <Paper
        elevation={0}
        sx={{
            p: 2.5,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            height: "100%",
            position: "relative",
            overflow: "hidden",
            bgcolor: "#fff",
            transition: "all .2s ease",
            "&:hover": {
                boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
                transform: "translateY(-2px)",
            },
            "&::before": {
                content: '""',
                position: "absolute",
                top: 0, left: 0, bottom: 0,
                width: 4,
                bgcolor: accent,
            },
        }}
    >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box sx={{ minWidth: 0 }}>
                <Typography
                    variant="overline"
                    color="text.secondary"
                    fontWeight={800}
                    sx={{ letterSpacing: 1, lineHeight: 1.2 }}
                >
                    {label}
                </Typography>
                <Typography variant="h5" fontWeight={900} sx={{ mt: 0.5 }}>
                    {value}
                </Typography>
                {subtitle && (
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
            <Box
                sx={{
                    flexShrink: 0,
                    width: 44, height: 44, borderRadius: 2.5,
                    display: "grid", placeItems: "center",
                    bgcolor: `${accent}14`,
                    color: accent,
                }}
            >
                <Icon />
            </Box>
        </Stack>
    </Paper>
);

const PaymentStagePill = ({ stage, facturada = false }) => {
    const cfg = PAYMENT_STAGES[stage];

    if (!cfg) {
        return (
            <Chip
                label="—"
                size="small"
                sx={{ bgcolor: "#f5f5f5", color: "#9e9e9e", fontWeight: 700 }}
            />
        );
    }

    const { Icon } = cfg;

    const content = (
        <Stack direction="row" alignItems="center" spacing={0.5}>
            <Chip
                icon={<Icon sx={{ fontSize: 14, color: `${cfg.color} !important` }} />}
                label={cfg.label}
                size="small"
                sx={{
                    bgcolor: cfg.bg,
                    color: cfg.color,
                    fontWeight: 800,
                    border: `1px solid ${cfg.color}33`,
                }}
            />
            {facturada && (
                <ReceiptLongIcon sx={{ fontSize: 16, color: "#455a64" }} />
            )}
        </Stack>
    );

    if (!facturada) return content;

    return (
        <Tooltip arrow placement="top" title="Factura emitida — pendiente de pago">
            <span>{content}</span>
        </Tooltip>
    );
};

const ValorizacionCell = ({ valorizacionId, valorizacionEstado }) => {
    if (!valorizacionId) {
        return (
            <Typography variant="caption" color="text.disabled" fontStyle="italic">
                Sin valorizar
            </Typography>
        );
    }

    const cfg = VALORIZACION_ESTADO_CONFIG[valorizacionEstado] || {
        label: valorizacionEstado || "—",
        color: "#757575",
        text: "#fff",
    };

    return (
        <Stack spacing={0.5} alignItems="flex-start">
            <Tooltip
                arrow
                placement="top"
                title={
                    valorizacionEstado
                        ? `Estado de la valorización: ${cfg.label}`
                        : "Valorización asociada"
                }
            >
                <Typography
                    variant="body2"
                    fontWeight={900}
                    color="primary"
                    sx={{ cursor: "help" }}
                >
                    {codigoVal(valorizacionId)}
                </Typography>
            </Tooltip>
            <Chip
                label={cfg.label}
                size="small"
                sx={{
                    bgcolor: cfg.color,
                    color: cfg.text,
                    fontWeight: 800,
                    height: 20,
                    fontSize: 10.5,
                    letterSpacing: 0.3,
                }}
            />
        </Stack>
    );
};

/* -------------------------------------------------------------------------- */
/*                                Componente                                  */
/* -------------------------------------------------------------------------- */

const ClientServiceOrdersDashboard = () => {
    const navigate = useNavigate();
    const { user } = useAuthContext();

    /* ---------- Proyectos del cliente ---------- */
    const clientProjects = useMemo(() => {
        const services = user?.ServiciosProyecto || [];
        const map = new Map();
        services.forEach((service) => {
            const proid = service.proid ?? service.ProID;
            const pronom = service.pronom ?? service.ProNom;
            if (proid && !map.has(Number(proid))) {
                map.set(Number(proid), { proid: Number(proid), pronom });
            }
        });
        return Array.from(map.values());
    }, [user]);

    /* ---------- Filtros ---------- */
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [search, setSearch] = useState("");
    const [status, setStatus] = useState("TODOS");
    const [situacion, setSituacion] = useState("TODOS");   // etapa de pago
    const [projectId, setProjectId] = useState("TODOS");
    const [fechaDesde, setFechaDesde] = useState("");
    const [fechaHasta, setFechaHasta] = useState("");

    const resetPage = () => setPage(0);

    const clearFilters = () => {
        setSearch("");
        setStatus("TODOS");
        setSituacion("TODOS");
        setProjectId("TODOS");
        setFechaDesde("");
        setFechaHasta("");
        setPage(0);
    };

    /* ---------- Data ---------- */
    const {
        serviceOrdersClient,
        total,
        isLoading,
        error,
    } = useClientServiceOrders({
        page: page + 1,
        limit: rowsPerPage,
        search,
        status: status === "TODOS" ? undefined : status,
        situacion: situacion === "TODOS" ? undefined : situacion,  // 👈 backend debe soportarlo
        projectId: projectId === "TODOS" ? undefined : projectId,
        fechaDesde: fechaDesde || undefined,
        fechaHasta: fechaHasta || undefined,
    });

    const serviceOrders = serviceOrdersClient || [];

    /* ---------- Métricas ----------
     *  ⚠️ NOTA: se calculan sobre la página actual.
     *  Si el backend ya devuelve sumatorias por etapa (recomendado),
     *  reemplaza estas líneas por esos valores para reflejar el total real.
     */
    const metrics = useMemo(() => {
        const acc = {
            ordenes: total || 0,
            pasajeros: 0,
            manifiestos: 0,
            porValorizar: 0,
            porCobrar: 0,
            cobrado: 0,
            countPorValorizar: 0,
            countPorCobrar: 0,
            countCobrado: 0,
        };

        serviceOrders.forEach((item) => {
            const monto = Number(item.ordsercabpre || 0);
            const stage = ESTADO_CONFIG[item.ordsercabest]?.stage;

            acc.pasajeros += Number(item.total_pasajeros || 0);
            acc.manifiestos += Number(item.total_manifiestos || 0);

            if (stage === "POR_VALORIZAR") {
                acc.porValorizar += monto;
                acc.countPorValorizar += 1;
            } else if (stage === "POR_COBRAR") {
                acc.porCobrar += monto;
                acc.countPorCobrar += 1;
            } else if (stage === "COBRADO") {
                acc.cobrado += monto;
                acc.countCobrado += 1;
            }
        });

        return acc;
    }, [serviceOrders, total]);

    if (error) {
        return (
            <Alert severity="error">
                No se pudieron cargar las órdenes de servicio del cliente.
            </Alert>
        );
    }

    const hasActiveFilters =
        Boolean(search) ||
        status !== "TODOS" ||
        situacion !== "TODOS" ||
        projectId !== "TODOS" ||
        Boolean(fechaDesde) ||
        Boolean(fechaHasta);

    return (
        <Paper sx={{ p: 3, bgcolor: "#fafafa", borderRadius: 3 }}>
            {/* ------------------------------ Header ------------------------------ */}
            <Box mb={3}>
                <Typography variant="h4" fontWeight={900} color="primary">
                    Mis órdenes de servicio
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Consulta de servicios, montos, pasajeros y situación de pago por proyecto.
                </Typography>
            </Box>

            {/* ------------------------ Leyenda de etapas -------------------------- */}
            <Alert
                icon={<InfoOutlinedIcon />}
                severity="info"
                sx={{
                    mb: 3, borderRadius: 2.5,
                    bgcolor: "#e5f4fd", color: "#0d3c61",
                    "& .MuiAlert-icon": { color: "#0288d1" },
                }}
            >
                <Typography variant="body2">
                    <b>Por valorizar:</b> servicios en ejecución o finalizados aún no valorizados.&nbsp;
                    <b>Por pagar:</b> servicios valorizados o facturados pendientes de pago.&nbsp;
                    <b>Pagado:</b> servicios cerrados con pago confirmado.
                </Typography>
            </Alert>

            {/* ------------------------ Métricas operativas ------------------------ */}
            <Grid container spacing={2} mb={2}>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        label="Órdenes"
                        value={formatNumber(metrics.ordenes)}
                        subtitle="Total según filtros"
                        icon={RouteIcon}
                        accent="#1976d2"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        label="Pasajeros"
                        value={formatNumber(metrics.pasajeros)}
                        subtitle="Personas transportadas"
                        icon={GroupsIcon}
                        accent="#388e3c"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        label="Unidades"
                        value={formatNumber(metrics.manifiestos)}
                        subtitle="Vehículos despachados"
                        icon={DirectionsBusIcon}
                        accent="#0288d1"
                    />
                </Grid>
            </Grid>

            {/* ------------------------ Métricas financieras ----------------------- */}
            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        label="Pendiente de valorizar"
                        value={formatCurrency(metrics.porValorizar)}
                        subtitle={`${metrics.countPorValorizar} orden(es) en ejecución / finalizadas`}
                        icon={HourglassEmptyIcon}
                        accent={PAYMENT_STAGES.POR_VALORIZAR.color}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        label="Pendiente de pago"
                        value={formatCurrency(metrics.porCobrar)}
                        subtitle={`${metrics.countPorCobrar} orden(es) valorizadas`}
                        icon={ReceiptLongIcon}
                        accent={PAYMENT_STAGES.POR_COBRAR.color}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                    <MetricCard
                        label="Pagado"
                        value={formatCurrency(metrics.cobrado)}
                        subtitle={`${metrics.countCobrado} orden(es) facturadas`}
                        icon={PaidIcon}
                        accent={PAYMENT_STAGES.COBRADO.color}
                    />
                </Grid>
            </Grid>

            {/* ------------------------------ Filtros ------------------------------ */}
            <Paper
                elevation={0}
                sx={{
                    mb: 3,
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "#fff",
                    overflow: "hidden",
                    boxShadow: "0 2px 12px rgba(15, 23, 42, 0.04)",
                }}
            >
                {/* Header con identidad visual */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    sx={{
                        px: 2.5,
                        py: 1.75,
                        bgcolor: "#f8fafc",
                        borderBottom: "1px solid",
                        borderColor: "divider",
                    }}
                >
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: 2,
                            display: "grid",
                            placeItems: "center",
                            bgcolor: "#1976d214",
                            color: "#1976d2",
                        }}
                    >
                        <TuneIcon fontSize="small" />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" fontWeight={800} lineHeight={1.15}>
                            Filtros
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Refina por proyecto, periodo, situación o estado.
                        </Typography>
                    </Box>

                    {hasActiveFilters && (
                        <Button
                            size="small"
                            startIcon={<FilterAltOffIcon fontSize="small" />}
                            onClick={clearFilters}
                            sx={{
                                ml: "auto",
                                textTransform: "none",
                                fontWeight: 700,
                                color: "text.secondary",
                                borderRadius: 2,
                                px: 1.5,
                                "&:hover": {
                                    color: "error.main",
                                    bgcolor: "rgba(211, 47, 47, 0.06)",
                                },
                            }}
                        >
                            Limpiar todo
                        </Button>
                    )}
                </Stack>

                {/* Cuerpo del panel */}
                <Box sx={{ p: 2.5 }}>
                    <Grid container spacing={2}>
                        {/* Búsqueda destacada */}
                        <Grid item xs={12} md={6}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Buscar por código, servicio o proyecto…"
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    resetPage();
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Proyecto</InputLabel>
                                <Select
                                    label="Proyecto"
                                    value={projectId}
                                    onChange={(e) => {
                                        setProjectId(e.target.value);
                                        resetPage();
                                    }}
                                >
                                    <MenuItem value="TODOS">Todos</MenuItem>
                                    {clientProjects.map((p) => (
                                        <MenuItem key={p.proid} value={p.proid}>
                                            {p.pronom}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Situación</InputLabel>
                                <Select
                                    label="Situación"
                                    value={situacion}
                                    onChange={(e) => {
                                        setSituacion(e.target.value);
                                        resetPage();
                                    }}
                                >
                                    <MenuItem value="TODOS">Todas</MenuItem>
                                    <MenuItem value="POR_VALORIZAR">Por valorizar</MenuItem>
                                    <MenuItem value="POR_COBRAR">Por pagar</MenuItem>
                                    <MenuItem value="COBRADO">Pagado (cerradas)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Desde"
                                value={fechaDesde}
                                onChange={(e) => {
                                    setFechaDesde(e.target.value);
                                    resetPage();
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={6} md={3}>
                            <TextField
                                fullWidth
                                size="small"
                                type="date"
                                label="Hasta"
                                value={fechaHasta}
                                onChange={(e) => {
                                    setFechaHasta(e.target.value);
                                    resetPage();
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Estado</InputLabel>
                                <Select
                                    label="Estado"
                                    value={status}
                                    onChange={(e) => {
                                        setStatus(e.target.value);
                                        resetPage();
                                    }}
                                >
                                    <MenuItem value="TODOS">Todos</MenuItem>
                                    <MenuItem value="EN_EJECUCION">En ejecución</MenuItem>
                                    <MenuItem value="FINALIZADA">Finalizada</MenuItem>
                                    <MenuItem value="PRE_VALORIZADA">Pre-valorizada</MenuItem>
                                    <MenuItem value="VALORIZADA">Valorizada</MenuItem>
                                    <MenuItem value="FACTURADA">Facturada</MenuItem>
                                    <MenuItem value="CERRADA">Cerrada</MenuItem>
                                    <MenuItem value="CANCELADA">Cancelada</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {/* Espacio para que la fila 2 quede equilibrada */}
                        <Grid item xs={12} md={3} />
                    </Grid>

                    {/* Chips de filtros activos */}
                    {hasActiveFilters && (
                        <Stack
                            direction="row"
                            alignItems="center"
                            flexWrap="wrap"
                            gap={0.75}
                            sx={{
                                mt: 2,
                                pt: 2,
                                borderTop: "1px dashed",
                                borderColor: "divider",
                            }}
                        >
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={700}
                                sx={{ mr: 0.5, textTransform: "uppercase", letterSpacing: 0.6 }}
                            >
                                Aplicados
                            </Typography>

                            {search && (
                                <Chip
                                    size="small"
                                    label={`Búsqueda: "${search}"`}
                                    onDelete={() => { setSearch(""); resetPage(); }}
                                    sx={{ fontWeight: 700 }}
                                />
                            )}

                            {projectId !== "TODOS" && (
                                <Chip
                                    size="small"
                                    label={`Proyecto: ${clientProjects.find((p) => Number(p.proid) === Number(projectId))?.pronom || projectId
                                        }`}
                                    onDelete={() => { setProjectId("TODOS"); resetPage(); }}
                                    sx={{ fontWeight: 700 }}
                                />
                            )}

                            {situacion !== "TODOS" && (
                                <Chip
                                    size="small"
                                    label={
                                        situacion === "POR_VALORIZAR"
                                            ? "Por valorizar"
                                            : situacion === "POR_COBRAR"
                                                ? "Por pagar"
                                                : "Pagado"
                                    }
                                    onDelete={() => { setSituacion("TODOS"); resetPage(); }}
                                    sx={{ fontWeight: 700 }}
                                />
                            )}

                            {status !== "TODOS" && (
                                <Chip
                                    size="small"
                                    label={`Estado: ${ESTADO_CONFIG[status]?.label || status}`}
                                    onDelete={() => { setStatus("TODOS"); resetPage(); }}
                                    sx={{ fontWeight: 700 }}
                                />
                            )}

                            {fechaDesde && (
                                <Chip
                                    size="small"
                                    label={`Desde: ${fechaDesde}`}
                                    onDelete={() => { setFechaDesde(""); resetPage(); }}
                                    sx={{ fontWeight: 700 }}
                                />
                            )}

                            {fechaHasta && (
                                <Chip
                                    size="small"
                                    label={`Hasta: ${fechaHasta}`}
                                    onDelete={() => { setFechaHasta(""); resetPage(); }}
                                    sx={{ fontWeight: 700 }}
                                />
                            )}
                        </Stack>
                    )}
                </Box>
            </Paper>

            {/* ------------------------------ Tabla ------------------------------- */}
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    border: "1px solid", borderColor: "divider",
                    borderRadius: 3, overflow: "hidden",
                }}
            >
                <Table size="small">
                    <TableHead sx={{ bgcolor: "#f5f7fa" }}>
                        <TableRow>
                            <TableCell><strong>Código</strong></TableCell>
                            <TableCell><strong>Proyecto / Servicio</strong></TableCell>
                            <TableCell><strong>Descripción</strong></TableCell>
                            <TableCell><strong>Periodo</strong></TableCell>
                            <TableCell align="center"><strong>Pax</strong></TableCell>
                            <TableCell align="center"><strong>Unid.</strong></TableCell>
                            <TableCell align="right"><strong>Monto</strong></TableCell>
                            <TableCell><strong>Estado</strong></TableCell>
                            <TableCell><strong>Valorización</strong></TableCell>
                            <TableCell><strong>Situación</strong></TableCell>
                            <TableCell align="center"><strong>Ver</strong></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {isLoading && (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        )}

                        {!isLoading && serviceOrders.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={11} align="center" sx={{ py: 6 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No se encontraron órdenes de servicio con los filtros seleccionados.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}

                        {!isLoading && serviceOrders.map((s) => {
                            const estado = ESTADO_CONFIG[s.ordsercabest];
                            const stage = estado?.stage;
                            const stageCfg = PAYMENT_STAGES[stage];
                            const monto = Number(s.ordsercabpre || 0);

                            return (
                                <TableRow key={s.ordsercabid} hover>
                                    <TableCell>
                                        <Typography fontWeight={900} color="primary" variant="body2">
                                            {codigoOS(s.ordsercabid)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {s.ordsercabtip}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2" fontWeight={700}>
                                            {s.pronom}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {s.serpronom}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Typography variant="body2">{s.ordsercabnom}</Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Stack spacing={0.2}>
                                            <Typography variant="body2" fontWeight={600}>
                                                {formatDate(s.ordsercabfec)}
                                            </Typography>
                                            {s.ordsercabfecfin && (
                                                <Typography variant="caption" color="text.secondary">
                                                    → {formatDate(s.ordsercabfecfin)}
                                                </Typography>
                                            )}
                                        </Stack>
                                    </TableCell>

                                    <TableCell align="center">
                                        <Chip
                                            label={formatNumber(s.total_pasajeros)}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                        />
                                    </TableCell>

                                    <TableCell align="center">
                                        <Chip
                                            label={formatNumber(s.total_manifiestos)}
                                            size="small"
                                            color="info"
                                            variant="outlined"
                                        />
                                    </TableCell>

                                    <TableCell align="right">
                                        <Typography
                                            fontWeight={800}
                                            sx={{ color: stageCfg?.color || "text.primary" }}
                                        >
                                            {formatCurrency(monto)}
                                        </Typography>
                                    </TableCell>

                                    <TableCell>
                                        <Chip
                                            label={estado?.label || s.ordsercabest}
                                            size="small"
                                            sx={{
                                                bgcolor: estado?.color || "grey.500",
                                                color: estado?.text || "#fff",
                                                fontWeight: 800,
                                                minWidth: 110,
                                            }}
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <ValorizacionCell
                                            valorizacionId={s.valorizacion_id}
                                            valorizacionEstado={s.valorizacion_estado}
                                        />
                                    </TableCell>

                                    <TableCell>
                                        <PaymentStagePill
                                            stage={stage}
                                            facturada={s.ordsercabest === "FACTURADA"}
                                        />
                                    </TableCell>

                                    <TableCell align="center">
                                        <Tooltip title="Ver detalle">
                                            <IconButton
                                                size="small"
                                                onClick={() =>
                                                    navigate(`/administracion/servicio2/${s.ordsercabid}/viajeros`)
                                                }
                                            >
                                                <VisibilityIcon sx={{ color: "#009688" }} />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={total}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                }}
                sx={{ mt: 2 }}
            />
        </Paper>
    );
};

export default ClientServiceOrdersDashboard;